/**
 * Feature flags (docs/PATH-V2.md §12.1 p.5; server: apps/server/src/accounts/features.ts): `GET /api/features` is
 * read ONCE at start, before the first paint (../main.tsx awaits `loadFeatures()`), so a switched-on feature never
 * flashes in after the old screen, nor the other way round. A sign-in and a sign-out reload the page
 * (./account/accountSession.ts), which reads the flags again for the new session; `loadFeatures()` may also be called
 * again by hand, and every `useFeature` follows.
 *
 * Every key is off until the answer came, and stays off when the server does not answer, answers late
 * (`LOAD_TIMEOUT_MS`), or answers something odd: an unreachable server never switches the new path on.
 *
 * A switch made on the server (`admin.ts feature set …`) reaches an open page when it becomes visible again
 * (`watchFeaturesOnVisible`, debounced: at most once per `REFRESH_MIN_INTERVAL_MS`). Such a re-read keeps what is in
 * force when it fails — a flaky network never flips the path off in the middle of a session.
 */
import { useSyncExternalStore } from 'react';
import { API_BASE, FEATURE_KEYS } from '@gambit/shared';
import type { FeatureKey, FeaturesView } from '@gambit/shared';

/** the start of the app waits at most this long for the flags (then: everything off) */
export const LOAD_TIMEOUT_MS = 3_000;

const allOff = (): FeaturesView => Object.fromEntries(FEATURE_KEYS.map((k) => [k, false])) as FeaturesView;

let current: FeaturesView = allOff();
const listeners = new Set<() => void>();

function publish(next: FeaturesView): void {
  if (FEATURE_KEYS.every((k) => next[k] === current[k])) return;
  current = next;
  for (const l of listeners) l();
}

/** The server's answer as a view: only `true` switches a known key on. Pure. */
export function parseFeatures(data: unknown): FeaturesView {
  const view = allOff();
  if (typeof data !== 'object' || data === null) return view;
  for (const k of FEATURE_KEYS) view[k] = (data as Record<string, unknown>)[k] === true;
  return view;
}

/**
 * Reads the flags of this session (never from the browser's cache) and publishes them; resolves with what is now in
 * force. Never throws.
 */
export async function loadFeatures(o: { fetchImpl?: typeof fetch; timeoutMs?: number; keepOnError?: boolean } = {}): Promise<FeaturesView> {
  const doFetch = o.fetchImpl ?? fetch;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), o.timeoutMs ?? LOAD_TIMEOUT_MS);
  let next: FeaturesView | null = null;
  try {
    const res = await doFetch(`${API_BASE}/features`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: abort.signal });
    if (res.ok) next = parseFeatures(await res.json());
  } catch {
    // offline, timed out, not JSON: everything off (or, for a re-read, what is in force)
  } finally {
    clearTimeout(timer);
  }
  if (next !== null) publish(next);
  else if (o.keepOnError !== true) publish(allOff());
  return current;
}

/** a page that comes back into view re-reads the flags at most this often */
export const REFRESH_MIN_INTERVAL_MS = 30_000;

export interface VisibilityRefreshDeps {
  /** the re-read (default: `loadFeatures({ keepOnError: true })`) */
  load?: () => Promise<unknown>;
  now?: () => number;
  doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
  minIntervalMs?: number;
}

/**
 * Re-reads the flags whenever the page becomes visible again — not more than once per `minIntervalMs`, never two at
 * once. The start (main.tsx) already read them: the first visible moment within the interval does nothing. Returns
 * the stop function.
 */
export function watchFeaturesOnVisible(deps: VisibilityRefreshDeps = {}): () => void {
  const doc = deps.doc ?? (typeof document === 'undefined' ? null : document);
  if (doc === null) return () => undefined;
  const now = deps.now ?? Date.now;
  const load = deps.load ?? (() => loadFeatures({ keepOnError: true }));
  const minInterval = deps.minIntervalMs ?? REFRESH_MIN_INTERVAL_MS;
  let last = now();
  let busy = false;
  const onVisibility = (): void => {
    if (doc.visibilityState !== 'visible' || busy || now() - last < minInterval) return;
    busy = true;
    last = now();
    void load()
      .catch(() => undefined)
      .finally(() => {
        busy = false;
      });
  };
  doc.addEventListener('visibilitychange', onVisibility);
  return () => doc.removeEventListener('visibilitychange', onVisibility);
}

/** The flag right now, outside React. */
export function featureOn(key: FeatureKey): boolean {
  return current[key];
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Is this feature on for the current session? false while loading and on any error. */
export function useFeature(key: FeatureKey): boolean {
  const get = (): boolean => current[key];
  return useSyncExternalStore(subscribe, get, get);
}

/** Tests only: back to «nothing loaded». */
export function resetFeaturesForTest(): void {
  publish(allOff());
}
